import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Alert,
  ALERT_LAYOUTS,
  ALERT_VARIANTS,
  type AlertAction,
  type AlertLayout,
  type AlertVariant,
  type ContextualMenuItem,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'

const TOAST_AUTO_DISMISS_MS = 5000
const TOAST_EXIT_MS = 300

const VARIANT_OPTIONS = ALERT_VARIANTS
const LAYOUT_OPTIONS = ALERT_LAYOUTS

const LAYOUT_LABELS: Record<AlertLayout, string> = {
  board: 'Board',
  toast: 'Toast',
}

const VARIANT_TITLES: Record<AlertVariant, string> = {
  critical: 'Critical Notification',
  urgent: 'Urgent Notification',
  info: 'Information Notification',
  success: 'Success!',
}

const formatVariantLabel = (variant: AlertVariant): string =>
  variant.charAt(0).toUpperCase() + variant.slice(1)

const AlertShowcase: React.FC = () => {
  const [variant, setVariant] = useState<AlertVariant>('critical')
  const [title, setTitle] = useState('Critical Notification')
  const [timestamp, setTimestamp] = useState('March 7, 2025 • 2:24 PM')
  const [description, setDescription] = useState(
    'This type of notification will require actions from a specific user that is needed to accomplish something important.',
  )
  const [showActions, setShowActions] = useState(true)
  const [showMenu, setShowMenu] = useState(true)
  const [primaryLabel, setPrimaryLabel] = useState('Action Button/CTA')
  const [secondaryLabel, setSecondaryLabel] = useState('Mark as Read')
  const [useCustomTitle, setUseCustomTitle] = useState(false)
  const [lastAction, setLastAction] = useState<string | null>(null)
  const [lastMenuAction, setLastMenuAction] = useState<string | null>(null)
  const [liveLayout, setLiveLayout] = useState<AlertLayout>('board')
  const [calledToast, setCalledToast] = useState<{
    key: number
    exiting: boolean
  } | null>(null)
  const dismissTimerRef = useRef<number | null>(null)
  const exitTimerRef = useRef<number | null>(null)

  const clearToastTimers = useCallback((): void => {
    if (dismissTimerRef.current != null) {
      window.clearTimeout(dismissTimerRef.current)
      dismissTimerRef.current = null
    }
    if (exitTimerRef.current != null) {
      window.clearTimeout(exitTimerRef.current)
      exitTimerRef.current = null
    }
  }, [])

  const dismissCalledToast = useCallback((): void => {
    setCalledToast((current) => {
      if (current == null || current.exiting) {
        return current
      }
      return { ...current, exiting: true }
    })
    exitTimerRef.current = window.setTimeout(() => {
      setCalledToast(null)
      exitTimerRef.current = null
    }, TOAST_EXIT_MS)
  }, [])

  const callToast = useCallback((): void => {
    clearToastTimers()
    setCalledToast({ key: Date.now(), exiting: false })
    dismissTimerRef.current = window.setTimeout(() => {
      dismissCalledToast()
      dismissTimerRef.current = null
    }, TOAST_AUTO_DISMISS_MS)
  }, [clearToastTimers, dismissCalledToast])

  useEffect(
    () => () => {
      clearToastTimers()
    },
    [clearToastTimers],
  )

  const handleActionClick = (label: string): void => {
    setLastAction(label)
  }

  const handleMenuSelect = (label: string): void => {
    setLastMenuAction(label)
  }

  const getMenuItems = useCallback(
    (variantOption: AlertVariant): ContextualMenuItem[] => {
      const onSelect =
        (label: string): (() => void) =>
        () => {
          handleMenuSelect(label)
        }

      switch (variantOption) {
        case 'critical':
        case 'urgent':
          return [
            { id: 'dismiss', label: 'Dismiss', onSelect: onSelect('Dismiss') },
            {
              id: 'snooze',
              label: 'Snooze 1 hour',
              onSelect: onSelect('Snooze 1 hour'),
            },
            {
              id: 'assign',
              label: 'Assign to billing',
              onSelect: onSelect('Assign to billing'),
            },
          ]
        case 'info':
          return [
            { id: 'dismiss', label: 'Dismiss', onSelect: onSelect('Dismiss') },
            {
              id: 'view-source',
              label: 'View source',
              onSelect: onSelect('View source'),
            },
            { id: 'pin', label: 'Pin', onSelect: onSelect('Pin') },
          ]
        case 'success':
          return [
            { id: 'dismiss', label: 'Dismiss', onSelect: onSelect('Dismiss') },
            {
              id: 'mute',
              label: "Don't show again",
              onSelect: onSelect("Don't show again"),
            },
          ]
        default:
          return []
      }
    },
    [],
  )

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Content',
        fields: [
          {
            id: 'alert-variant',
            label: 'Highlighted variant',
            control: (
              <select
                id="alert-variant"
                className="ui-field-control"
                value={variant}
                onChange={(event) =>
                  setVariant(event.target.value as AlertVariant)
                }
              >
                {VARIANT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {formatVariantLabel(option)}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'alert-layout',
            label: 'Layout',
            control: (
              <select
                id="alert-layout"
                className="ui-field-control"
                value={liveLayout}
                onChange={(event) =>
                  setLiveLayout(event.target.value as AlertLayout)
                }
              >
                {LAYOUT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {LAYOUT_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'alert-custom-title',
            label: 'Custom title',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="alert-custom-title"
                  type="checkbox"
                  checked={useCustomTitle}
                  onChange={(event) => setUseCustomTitle(event.target.checked)}
                />
                <span>Same title for all variants</span>
              </label>
            ),
          },
          {
            id: 'alert-title',
            label: 'Title',
            control: (
              <input
                id="alert-title"
                className="ui-field-control"
                type="text"
                value={title}
                disabled={!useCustomTitle}
                onChange={(event) => setTitle(event.target.value)}
              />
            ),
          },
          {
            id: 'alert-timestamp',
            label: 'Timestamp',
            control: (
              <input
                id="alert-timestamp"
                className="ui-field-control"
                type="text"
                value={timestamp}
                onChange={(event) => setTimestamp(event.target.value)}
              />
            ),
          },
          {
            id: 'alert-description',
            label: 'Description',
            control: (
              <textarea
                id="alert-description"
                className="ui-field-control ui-field-control--textarea"
                rows={4}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            ),
          },
        ],
      },
      {
        title: 'Actions',
        fields: [
          {
            id: 'alert-show-actions',
            label: 'Show actions',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="alert-show-actions"
                  type="checkbox"
                  checked={showActions}
                  onChange={(event) => setShowActions(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'alert-secondary-label',
            label: 'Secondary label (board)',
            control: (
              <input
                id="alert-secondary-label"
                className="ui-field-control"
                type="text"
                value={secondaryLabel}
                disabled={!showActions}
                onChange={(event) => setSecondaryLabel(event.target.value)}
              />
            ),
          },
          {
            id: 'alert-primary-label',
            label: 'Primary label',
            control: (
              <input
                id="alert-primary-label"
                className="ui-field-control"
                type="text"
                value={primaryLabel}
                disabled={!showActions}
                onChange={(event) => setPrimaryLabel(event.target.value)}
              />
            ),
          },
          {
            id: 'alert-show-menu',
            label: 'Show menu',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="alert-show-menu"
                  type="checkbox"
                  checked={showMenu}
                  onChange={(event) => setShowMenu(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [
      variant,
      useCustomTitle,
      title,
      timestamp,
      description,
      showActions,
      showMenu,
      liveLayout,
      primaryLabel,
      secondaryLabel,
    ],
  )

  const getAlertTitle = (variantOption: AlertVariant): string =>
    useCustomTitle ? title : VARIANT_TITLES[variantOption]

  const getActions = (
    layout: AlertLayout,
    variantOption: AlertVariant,
  ): {
    primaryAction?: AlertAction
    secondaryAction?: AlertAction
  } => {
    if (!showActions) {
      return {}
    }

    // Board critical/urgent = secondary + primary; toast urgent = secondary only;
    // info/success action rows = primary; board info without actions when toggled off.
    const showSecondary =
      (layout === 'board' &&
        (variantOption === 'critical' || variantOption === 'urgent')) ||
      (layout === 'toast' && variantOption === 'urgent')
    const showPrimary = !(layout === 'toast' && variantOption === 'urgent')

    return {
      primaryAction: showPrimary
        ? {
            label: primaryLabel,
            onClick: () => handleActionClick(primaryLabel),
          }
        : undefined,
      secondaryAction: showSecondary
        ? {
            label:
              layout === 'toast' && variantOption === 'urgent'
                ? primaryLabel
                : secondaryLabel,
            onClick: () =>
              handleActionClick(
                layout === 'toast' && variantOption === 'urgent'
                  ? primaryLabel
                  : secondaryLabel,
              ),
          }
        : undefined,
    }
  }

  return (
    <>
      <UiPreviewCanvas
        title="Alert"
        description="Board and toast layouts for light and."
        stageClassName="ui-preview__stage--wide alert-showcase__stage-wrap"
      >
        <div className={`alert-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Inspect the alert below. Use the trigger to see how a toast
              appears when called — same slide-in as NotificationToast.
            </p>
            <div className="ui-showcase-live__content ui-showcase-live__content--full">
              <Alert
                variant={variant}
                layout={liveLayout}
                title={getAlertTitle(variant)}
                timestamp={timestamp}
                description={description}
                showActions={showActions}
                showMenu={showMenu}
                menuItems={showMenu ? getMenuItems(variant) : undefined}
                {...getActions(liveLayout, variant)}
              />
            </div>
            <p className="alert-showcase__trigger-row">
              <button
                type="button"
                className="alert-showcase__trigger-link"
                onClick={callToast}
              >
                Call toast notification
              </button>
              <span className="alert-showcase__trigger-hint">
                Slides in from the right, auto-dismisses after 5s
              </span>
            </p>
            {lastAction || lastMenuAction ? (
              <div className="alert-showcase__feedback">
                {lastAction ? (
                  <p className="alert-showcase__action-note">
                    Last action: {lastAction}
                  </p>
                ) : null}
                {lastMenuAction ? (
                  <p className="alert-showcase__action-note">
                    Last menu item: {lastMenuAction}
                  </p>
                ) : null}
              </div>
            ) : null}
          </section>

          {calledToast && typeof document !== 'undefined'
            ? createPortal(
                <div className="alert-showcase__toast-host" aria-live="polite">
                  <Alert
                    key={calledToast.key}
                    variant={variant}
                    layout="toast"
                    title={getAlertTitle(variant)}
                    timestamp={timestamp}
                    description={description}
                    showActions={showActions}
                    showMenu={false}
                    animate
                    exiting={calledToast.exiting}
                    {...getActions('toast', variant)}
                  />
                </div>,
                document.body,
              )
            : null}

          <div className="alert-showcase__sheet-header">
            <span className="alert-showcase__sheet-header-label">Variant</span>
            {LAYOUT_OPTIONS.map((layout) => (
              <span
                key={layout}
                className="alert-showcase__sheet-header-label alert-showcase__sheet-header-label--layout"
              >
                {LAYOUT_LABELS[layout]}
              </span>
            ))}
          </div>

          {VARIANT_OPTIONS.map((variantOption) => (
            <div
              key={variantOption}
              className={`alert-showcase__row${
                variantOption === variant
                  ? ' alert-showcase__row--selected'
                  : ''
              }`}
            >
              <span className="alert-showcase__row-label">
                {formatVariantLabel(variantOption)}
              </span>
              {LAYOUT_OPTIONS.map((layout) => (
                <div key={layout} className="alert-showcase__cell">
                  <Alert
                    variant={variantOption}
                    layout={layout}
                    title={getAlertTitle(variantOption)}
                    timestamp={timestamp}
                    description={description}
                    showActions={showActions}
                    showMenu={showMenu}
                    menuItems={
                      showMenu ? getMenuItems(variantOption) : undefined
                    }
                    {...getActions(layout, variantOption)}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="Alert" sections={propertySections} />
    </>
  )
}

export default AlertShowcase
